import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import type { PlayerIndex } from '@tpubg/shared';
import { AdSlot } from '../ads/AdSlot';
import { FullscreenButton } from '../components/FullscreenButton';
import { GameActions } from '../components/LikeButton';
import { FitCanvas } from '../components/FitCanvas';
import { ModePicker, type PlayMode } from '../components/ModePicker';
import { CPU_LEVEL_LABEL, CPU_PLAYER, parseLevel, type CpuSetting } from '../lib/cpu';
import { useCpuLabels } from '../lib/useCpuLabels';
import { SoundToggle } from '../components/SoundToggle';
import { NextUp } from '../components/NextUp';
import { sound } from '../lib/audio';
import { isTouchOnly } from '../lib/motion';
import { TouchAction, TouchStick } from './TouchPad';
import { PLAYER_STYLES } from '../lib/players';
import { useStore } from '../lib/store';
import { keyLabel, loadBindings, saveBindings, type Action, type Bindings } from '../input/bindings';
import { KeyboardController } from '../input/keyboard';
import { ControlsEditor } from '../ui/ControlsEditor';
import { Countdown, Panel, PlayerTag, useAutoFocus } from '../ui/Overlays';
import type { CatalogEntry } from '../games/catalog';
import { ArcadeRunner, type ArcadeHud, type PauseReason } from './ArcadeRunner';
import type { ArcadeGameDef, PadKey } from './types';
import { setGameMeta } from '../lib/seo';

const PAD_TO_ACTION: Record<PadKey, Action> = { up: 'forward', down: 'back', left: 'left', right: 'right', action: 'fire' };

function labelsFor(def: ArcadeGameDef<unknown>): Partial<Record<Action, string>> {
  const out: Partial<Record<Action, string>> = {};
  for (const [k, v] of Object.entries(def.keys)) out[PAD_TO_ACTION[k as PadKey]] = v;
  return out;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function ArcadeShell({ entry, def }: { entry: CatalogEntry; def: ArcadeGameDef<any> }) {
  const [bindings, setBindings] = useState<Bindings>(loadBindings);
  const [playing, setPlaying] = useState(false);
  const [params, setParams] = useSearchParams();
  const mode: PlayMode = { vsCpu: !!def.cpu && params.get('vs') !== '2p', level: parseLevel(params.get('level')) };
  const cpu: CpuSetting | null = mode.vsCpu ? { player: CPU_PLAYER, level: mode.level } : null;
  useCpuLabels(cpu?.player ?? null);
  if (playing)
    return (
      <ArcadeMatch entry={entry} def={def} bindings={bindings} cpu={cpu} onSetup={() => setPlaying(false)} />
    );
  return (
    <ArcadeSetup
      entry={entry}
      def={def}
      mode={mode}
      onMode={(m) => setParams(m.vsCpu ? { vs: 'cpu', level: m.level } : { vs: '2p' }, { replace: true })}
      bindings={bindings}
      onBindings={(b) => {
        setBindings(b);
        saveBindings(b);
      }}
      onStart={() => setPlaying(true)}
    />
  );
}

function ArcadeSetup(props: {
  entry: CatalogEntry;
  def: ArcadeGameDef<unknown>;
  mode: PlayMode;
  onMode: (m: PlayMode) => void;
  bindings: Bindings;
  onBindings: (b: Bindings) => void;
  onStart: () => void;
}) {
  const { entry, def } = props;
  const touchOnly = isTouchOnly();
  const startRef = useAutoFocus<HTMLButtonElement>(!touchOnly);
  return (
    <main className="page setup game-page" id="main">
      <div className="setup__head setup__head--game">
        <span className="game-icon game-icon--lg" aria-hidden="true">
          <entry.Icon />
        </span>
        <div>
          <p className="eyebrow">
            {entry.categoryLabel} · {props.mode.vsCpu ? `vs Computer (${CPU_LEVEL_LABEL[props.mode.level]})` : 'Same Computer'}
          </p>
          <h1 className="setup__title">{entry.name}</h1>
          <p className="muted">{entry.tagline}</p>
        </div>
      </div>
      {touchOnly && (
        <div className="notice notice--warn" role="note">
          <strong>Touch controls are on:</strong> move with the joystick on the left and press the big button on the right. Two players on one device needs a keyboard, so play vs Bot here.
        </div>
      )}
      {def.cpu && <ModePicker value={props.mode} onChange={props.onMode} twoPlayerLabel="2 Players" />}
      <div className="setup__grid">
        <section className="card" aria-labelledby="controls-h">
          <h2 id="controls-h" className="card__title">
            Controls
          </h2>
          {props.mode.vsCpu && <p className="muted small">Playing solo: you’re Player 1 and can use either set of keys. The Bot plays Player 2.</p>}
          <ControlsEditor bindings={props.bindings} onChange={props.onBindings} labels={labelsFor(def)} />
        </section>
        <section className="card" aria-labelledby="rules-h">
          <h2 id="rules-h" className="card__title">
            How to play
          </h2>
          <ul className="rules">
            {def.rules.map((r) => (
              <li key={r}>{r}</li>
            ))}
            <li>
              First to <strong>{def.winScore}</strong> {def.unit}s wins. <kbd>Esc</kbd> pauses.
            </li>
          </ul>
        </section>
      </div>
      <div className="setup__actions">
        <button
          ref={startRef}
          type="button"
          className="btn btn--primary btn--large"
          onClick={() => {
            sound.unlock();
            props.onStart();
          }}
        >
          Start match
        </button>
        <Link to="/" className="btn btn--ghost">
          All games
        </Link>
      </div>
      <AdSlot placement="setup" />
    </main>
  );
}

function ArcadeScoreboard({ hud, unit, winScore, names }: { hud: ArcadeHud; unit: string; winScore: number; names: [string, string] }) {
  const side = (i: PlayerIndex) => {
    const s = PLAYER_STYLES[i];
    const score = i === 0 ? hud.score0 : hud.score1;
    return (
      <div className={`score score--p${i + 1}`} aria-label={`${s.label}: ${score} of ${winScore}`}>
        <span className="score__tag" aria-hidden="true">
          <span className="score__symbol">{s.symbol}</span>
          {s.label}
        </span>
        {names[i] !== s.label && <span className="score__name">{names[i]}</span>}
        <span className="score__value" aria-hidden="true">
          {score}
        </span>
      </div>
    );
  };
  return (
    <div className="scoreboard">
      {side(0)}
      <div className="scoreboard__mid">
        <span className="scoreboard__round">First to {winScore}</span>
        <span className="scoreboard__time">{hud.center ?? `${unit === 'point' ? 'Point' : 'Round'} ${Math.max(1, hud.round)}`}</span>
      </div>
      {side(1)}
    </div>
  );
}

function ArcadeMatch({
  entry,
  def,
  bindings,
  cpu,
  onSetup,
}: {
  entry: CatalogEntry;
  def: ArcadeGameDef<unknown>;
  bindings: Bindings;
  cpu: CpuSetting | null;
  onSetup: () => void;
}) {
  const names: [string, string] = cpu ? (cpu.player === 1 ? ['You', 'Bot'] : ['Bot', 'You']) : ['Player 1', 'Player 2'];
  const navigate = useNavigate();
  const rootRef = useRef<HTMLElement>(null);
  const [{ runner, keyboard }] = useState(() => {
    const holder: { runner?: ArcadeRunner<unknown> } = {};
    const kb = new KeyboardController(bindings, {
      isActive: () => holder.runner?.acceptingInput ?? false,
      onEscape: () => holder.runner?.togglePause(),
    });
    holder.runner = new ArcadeRunner(def, kb, cpu);
    return { runner: holder.runner, keyboard: kb };
  });
  const hud = useStore(runner.hud);
  const pause = useStore(runner.pause);

  useEffect(() => {
    setGameMeta(entry);
    keyboard.attach();
    runner.start();
    return () => {
      runner.stop();
      keyboard.detach();
    };
  }, [runner, keyboard, entry.name]);

  useEffect(() => {
    if (pause === null && hud.phase !== 'matchEnd') {
      (document.activeElement as HTMLElement | null)?.blur?.();
      rootRef.current?.focus({ preventScroll: true });
    }
  }, [pause, hud.phase]);

  // Phones and tablets get an on-screen joystick + action button for Player 1.
  const touch = isTouchOnly();
  const keysFor = (p: PlayerIndex) =>
    (Object.keys(def.keys) as PadKey[]).map((k) => keyLabel(bindings[p][PAD_TO_ACTION[k]])).join(' ');

  return (
    <main className={`play game-page${touch ? ' play--touch' : ''}`} id="main" ref={rootRef} tabIndex={-1} aria-label={`${entry.name} match`}>
      <div className="play__bar">
        <ArcadeScoreboard hud={hud} unit={def.unit} winScore={def.winScore} names={names} />
        <button type="button" className="icon-btn icon-btn--labelled" onClick={() => runner.setPaused('user')} disabled={hud.phase === 'matchEnd'}>
          <span aria-hidden="true">❚❚</span> Pause
        </button>
      </div>
      {touch && (
        <div className="touch-zone touch-zone--stick">
          <TouchStick kb={keyboard} keys={bindings[0]} used={def.keys} />
        </div>
      )}
      {touch && (
        <div className="touch-zone touch-zone--fire">
          {def.keys.action && <TouchAction kb={keyboard} code={bindings[0].fire} label={shortLabel(def.keys.action)} />}
        </div>
      )}
      <div className="play__stage">
        <FitCanvas width={def.width} height={def.height} label={`${entry.name} play area`} onCanvas={(c) => runner.setCanvas(c)} onResize={(w) => runner.resize(w)}>
          {pause === null && hud.countdown !== null && <Countdown n={hud.countdown} />}
          {pause === null && hud.phase === 'roundEnd' && hud.bannerWinner !== undefined && (
            <div className="overlay overlay--clear" aria-live="polite">
              <div className="banner banner--placed" style={{ top: `${(def.bannerY ?? 0.5) * 100}%` }}>
                {hud.bannerWinner !== null ? <PlayerTag index={hud.bannerWinner} /> : null} {hud.bannerText ?? (hud.bannerWinner !== null ? `scores!` : 'Draw')}
              </div>
            </div>
          )}
          {hud.phase === 'matchEnd' && hud.winner !== null && (
            <MatchEnd entry={entry} hud={hud} cpuPlayer={cpu?.player ?? null} onRematch={() => runner.restart()} onSetup={onSetup} onMenu={() => navigate('/')} />
          )}
          {pause !== null && <PauseMenu reason={pause} onResume={() => runner.setPaused(null)} onRestart={() => runner.restart()} onSetup={onSetup} onMenu={() => navigate('/')} />}
        </FitCanvas>
      </div>
      <div className="game-bar game-bar--arcade">
        <span className="game-bar__name">{entry.name}</span>
        <GameActions
          gameId={entry.id}
          path={entry.path}
          controls={
            <>
      {cpu ? (
        <p className="controls-summary">
          <span className="controls-summary__p controls-summary__p--p1">
            <strong>
              <span aria-hidden="true">●</span> You
            </strong>{' '}
            {keysFor(0)} or {keysFor(1)}
          </span>
          <span>Esc pause</span>
        </p>
      ) : (
      <p className="controls-summary">
        <span className="controls-summary__p controls-summary__p--p1">
          <strong>
            <span aria-hidden="true">●</span> P1
          </strong>{' '}
          {keysFor(0)}
        </span>
        <span className="controls-summary__p controls-summary__p--p2">
          <strong>
            <span aria-hidden="true">◆</span> P2
          </strong>{' '}
          {keysFor(1)}
        </span>
        <span>Esc pause</span>
      </p>
      )}
            </>
          }
        />
        <FullscreenButton target={rootRef} compact />
      </div>
    </main>
  );
}

function MatchEnd({
  entry,
  hud,
  cpuPlayer,
  onRematch,
  onSetup,
  onMenu,
}: {
  entry: CatalogEntry;
  hud: ArcadeHud;
  cpuPlayer: PlayerIndex | null;
  onRematch: () => void;
  onSetup: () => void;
  onMenu: () => void;
}) {
  const ref = useAutoFocus<HTMLButtonElement>();
  const w = hud.winner!;
  return (
    <Panel
      title={cpuPlayer === null ? `${PLAYER_STYLES[w].label} wins the match!` : w === cpuPlayer ? 'The Bot wins the match' : 'You win the match! 🎉'}
      tone={w === 0 ? 'p1' : 'p2'}
    >
      <p className="panel__lead">
        <span className="final-score">
          {hud.score0} – {hud.score1}
        </span>
      </p>
      <div className="panel__actions">
        <button ref={ref} type="button" className="btn btn--primary" onClick={onRematch}>
          Rematch
        </button>
        <button type="button" className="btn btn--ghost" onClick={onSetup}>
          Controls
        </button>
        <button type="button" className="btn btn--ghost" onClick={onMenu}>
          All games
        </button>
      </div>
      <NextUp entry={entry} panel />
    </Panel>
  );
}

function PauseMenu(props: { reason: PauseReason; onResume: () => void; onRestart: () => void; onSetup: () => void; onMenu: () => void }) {
  const ref = useAutoFocus<HTMLButtonElement>();
  return (
    <Panel title="Paused">
      {props.reason === 'focus' && <p className="panel__lead">The game paused because the window lost focus.</p>}
      <div className="panel__actions panel__actions--stack">
        <button ref={ref} type="button" className="btn btn--primary" onClick={props.onResume}>
          Resume <kbd>Esc</kbd>
        </button>
        <button type="button" className="btn btn--ghost" onClick={props.onRestart}>
          Restart match
        </button>
        <SoundToggle variant="full" />
        <button type="button" className="btn btn--ghost" onClick={props.onSetup}>
          Controls
        </button>
        <button type="button" className="btn btn--ghost" onClick={props.onMenu}>
          All games
        </button>
      </div>
    </Panel>
  );
}

/**
 * Button text from the game's key description, e.g. "Hold to shoot, let go…" → "Shoot",
 * "Tap: throw · Hold: scoop" → "Throw", "Pitch: start the windup" → "Pitch".
 */
export function shortLabel(desc: string): string {
  let seg = desc.split(/[·/(,]/)[0].trim();
  const role = seg.match(/^([A-Za-z]+):\s*(.*)$/);
  if (role) seg = /^(tap|hold|press)$/i.test(role[1]) ? role[2] : role[1];
  seg = seg.replace(/^(hold|tap|press)\s+(to\s+)?/i, '').trim();
  if (seg.length > 11) seg = seg.split(' ')[0];
  return seg ? seg[0].toUpperCase() + seg.slice(1) : 'Go';
}
